/**
 * src/game/progression/boss-weapon-unlocks.ts
 *
 * 打贏魔王 → 解鎖一種「武器類型」(玩遊戲軌「廣度」)。15 隻魔王各對應一種武器。
 *
 * ── 雙軌進度鐵則(CLAUDE.md 最高優先 / PROGRESSION_AND_ACCOUNTS.md §3.2 / §7)──
 *   - 解鎖的是「**可使用該武器的資格**」(廣度),寫進 progress.unlockedWeapons
 *     (比照 unlockedHeroes / unlockedAccessories 的玩遊戲軌欄位)。
 *   - **絕不**寫 factionXP / freeXP / skillLevels / 永久傷害。
 *   - 武器的**戰鬥效果走單場**:解鎖後該武器進入「場內升級牌池」(upgrade-pool 的
 *     weaponCardsFor),玩家在 run 中抽到才生效,run 結束 `new RunModifiers()` 歸零。
 *     沿用既有 RunModifiers 單場機制(與 R-004 C「場內武器進化」同精神 = 合法)。
 *   - 各武器彼此是 **sidegrade**(不同彈道/玩法,整體強度相當),不是「越解越強」。
 *     判準:解鎖不能讓「同一關更容易打贏」,只能「多一種玩法」。
 *
 * ── 為何是「廣度非戰力」──
 *   武器效果全部複用既有單場旗標(spreadBulletBonus / bounceCount / pierceCount /
 *   spreadBlastRadius / explodeOnHitRadius / bulletSizeMult),這些旗標每場
 *   `new RunModifiers()` 歸零、絕不持久化。解鎖只是讓「這張卡會出現在三選一池」;
 *   不解鎖就抽不到,但解鎖了也得在 run 中抽到才生效,且效果跨場不累積。因此打贏魔王
 *   得到的永久收益 = 0 戰力,只是「多一種可選玩法」(sidegrade 不同彈道,強度相當)。
 *
 * 純資料 + 純函式,無 Phaser / DOM 依賴,可單元測試。boss id 清單與
 * boss-pool.ts / boss-tier2-pool.ts / boss-tier3-pool.ts / boss-extra-pool.ts 對齊
 * (15 隻),但**不 import** 那些檔(只在此處硬寫 id 清單,避免耦合魔王平衡欄位)。
 */
import type { RunModifiers } from '../systems/run-modifiers';

/**
 * 一種武器類型的定義。`applyRunMod` 只寫單場 RunModifiers 旗標(複用既有 scene
 * 已施工的彈道機制),run 結束歸零。鐵則:絕不碰 progress/factionXP/freeXP。
 */
export interface WeaponType {
  /** 穩定 id(= progress.unlockedWeapons 內容 + upgrade-pool 卡 id)。 */
  id: string;
  /** 顯示名(結算「解鎖新武器:XXX」+ 升級卡標題)。 */
  displayName: string;
  emoji: string;
  /** 一句話玩法描述(升級卡 description)。 */
  description: string;
  /**
   * 套用單場效果到 RunModifiers(只寫既有 wired 旗標)。
   * 鐵則:純當局,跨場 `new RunModifiers()` 歸零;絕不寫 progress。
   */
  applyRunMod: (m: RunModifiers) => void;
}

// ── 15 種武器類型(sidegrade:不同彈道/玩法,整體強度相當)─────────────────────
//
// 全部複用 scene 已施工的單場旗標。每種給「一個鮮明的彈道特徵」,讓玩法明顯不同但
// 強度相當 —— 不是線性堆數值,而是「換一種打法」:
//   spreadBulletBonus = 散射(廣覆蓋,單發傷害不變)
//   pierceCount       = 貫穿(穿一整排,適合直線敵群)
//   bounceCount       = 彈跳(命中後轉向另一隻)
//   spreadBlastRadius = 子彈自帶小爆(per-bullet,落點波及)
//   explodeOnHitRadius= 命中點 AoE 爆炸(run-wide)
//   bulletSizeMult    = 巨彈(更易命中,不加傷害)

export const WEAPON_TYPES: readonly WeaponType[] = [
  // ── Tier 1 (BOSS_POOL) ──────────────────────────────────────────────────────
  {
    id: 'weapon-scatter-shot',
    displayName: '散射霰彈',
    emoji: '🎯',
    description: '多打 3 顆扇形子彈(廣覆蓋)',
    applyRunMod: (m) => { m.spreadBulletBonus += 3; },
  },
  {
    id: 'weapon-ricochet',
    displayName: '彈跳迴旋彈',
    emoji: '🪀',
    description: '子彈命中後彈向另一隻敵人',
    applyRunMod: (m) => { m.bounceCount += 2; },
  },
  {
    id: 'weapon-rail-lance',
    displayName: '貫穿軌道槍',
    emoji: '🔱',
    description: '子彈穿透敵人不消失(穿一整排)',
    applyRunMod: (m) => { m.pierceCount += 2; },
  },

  // ── Tier 2 (TIER2_BOSS_POOL) ────────────────────────────────────────────────
  {
    id: 'weapon-frag-grenade',
    displayName: '爆裂榴彈',
    emoji: '💣',
    description: '命中點範圍爆炸(波及周圍)',
    applyRunMod: (m) => { m.explodeOnHitRadius += 40; },
  },
  {
    id: 'weapon-heavy-slug',
    displayName: '重型獨頭彈',
    emoji: '🔵',
    description: '子彈變超大(更易命中)',
    applyRunMod: (m) => { m.bulletSizeMult *= 2.2; },
  },
  {
    id: 'weapon-pierce-volley',
    displayName: '穿甲散射',
    emoji: '➿',
    description: '多打 2 顆 + 全部穿透 1 次',
    applyRunMod: (m) => { m.spreadBulletBonus += 2; m.pierceCount += 1; },
  },
  {
    id: 'weapon-cluster-spread',
    displayName: '集束散彈',
    emoji: '🕸️',
    description: '多打 2 顆 + 每顆落點小爆',
    applyRunMod: (m) => { m.spreadBulletBonus += 2; m.spreadBlastRadius += 24; },
  },

  // ── Tier 3 (BOSS_TIER3_POOL) ────────────────────────────────────────────────
  {
    id: 'weapon-cluster-bomb',
    displayName: '集束炸彈',
    emoji: '🧨',
    description: '命中大範圍爆炸(清場)',
    applyRunMod: (m) => { m.explodeOnHitRadius += 60; },
  },
  {
    id: 'weapon-judgement-ray',
    displayName: '天命審判炮',
    emoji: '⚡',
    description: '貫穿巨彈 + 彈跳 2 次',
    applyRunMod: (m) => { m.bulletSizeMult *= 1.8; m.pierceCount += 1; m.bounceCount += 2; },
  },

  // ── Extra (BOSS_EXTRA_POOL) ─────────────────────────────────────────────────
  {
    id: 'weapon-web-shot',
    displayName: '蛛網散射',
    emoji: '🕷️',
    description: '多打 3 顆 + 彈跳 1 次',
    applyRunMod: (m) => { m.spreadBulletBonus += 3; m.bounceCount += 1; },
  },
  {
    id: 'weapon-hover-blast',
    displayName: '懸浮爆破彈',
    emoji: '🛸',
    description: '大子彈 + 命中爆炸',
    applyRunMod: (m) => { m.bulletSizeMult *= 1.7; m.explodeOnHitRadius += 30; },
  },
  {
    id: 'weapon-rail-cannon',
    displayName: '軌道列車炮',
    emoji: '🚄',
    description: '巨彈 + 穿透 3 次(直線清排)',
    applyRunMod: (m) => { m.bulletSizeMult *= 2.0; m.pierceCount += 3; },
  },
  {
    id: 'weapon-drill-spread',
    displayName: '鑽頭散彈',
    emoji: '🛠️',
    description: '多打 2 顆穿透大子彈',
    applyRunMod: (m) => { m.spreadBulletBonus += 2; m.bulletSizeMult *= 1.5; m.pierceCount += 1; },
  },
  {
    id: 'weapon-skull-mine',
    displayName: '骷髏地雷彈',
    emoji: '💀',
    description: '命中超大範圍爆炸',
    applyRunMod: (m) => { m.explodeOnHitRadius += 50; },
  },
  {
    id: 'weapon-crystal-prism',
    displayName: '水晶稜鏡彈',
    emoji: '💎',
    description: '多打 4 顆 + 彈跳 1 次(萬花筒)',
    applyRunMod: (m) => { m.spreadBulletBonus += 4; m.bounceCount += 1; },
  },
];

/** 依 weapon id 查 WeaponType(找不到回 undefined)。 */
export function weaponTypeById(weaponId: string): WeaponType | undefined {
  return WEAPON_TYPES.find((w) => w.id === weaponId);
}

/**
 * 全部 15 隻魔王 → 武器類型 id 的對應表。
 *
 * key = boss id(對齊 BOSS_POOL + TIER2_BOSS_POOL + BOSS_TIER3_POOL + BOSS_EXTRA_POOL)。
 * 此處硬寫 id 清單(不 import boss-pool 檔),避免耦合魔王平衡數值欄位(嚴格邊界:
 * 另一隻 agent 在調魔王平衡 bosses.json / boss def 數值)。
 *
 * tutorial-boss 不在 15 隻正式魔王內(教學 boss),刻意不給武器解鎖。
 */
export const BOSS_WEAPON_UNLOCK: Readonly<Record<string, string>> = {
  // Tier 1 (BOSS_POOL)
  'shu-mo': 'weapon-scatter-shot',      // 數魔・乘九 → 散射霰彈
  'fu-hun': 'weapon-ricochet',          // 符魂・舌劍 → 彈跳迴旋彈
  'yuan-su': 'weapon-rail-lance',       // 元素守衛   → 貫穿軌道槍
  // Tier 2 (TIER2_BOSS_POOL)
  'shu-fu': 'weapon-frag-grenade',      // 數符雙煞   → 爆裂榴彈
  'ji-yuan': 'weapon-heavy-slug',       // 機元雙影   → 重型獨頭彈
  'xing-yu': 'weapon-pierce-volley',    // 星域守衛   → 穿甲散射
  'zi-fu': 'weapon-cluster-spread',     // 自符魂使   → 集束散彈
  // Tier 3 (BOSS_TIER3_POOL)
  'shi-zhong': 'weapon-cluster-bomb',   // 期中總魔   → 集束炸彈
  'tian-ming': 'weapon-judgement-ray',  // 期末王     → 天命審判炮
  // Extra (BOSS_EXTRA_POOL)
  'spider': 'weapon-web-shot',          // 蜘蛛       → 蛛網散射
  'hover': 'weapon-hover-blast',        // 懸浮       → 懸浮爆破彈
  'train': 'weapon-rail-cannon',        // 列車       → 軌道列車炮
  'drill': 'weapon-drill-spread',       // 鑽頭       → 鑽頭散彈
  'skull': 'weapon-skull-mine',         // 骷髏       → 骷髏地雷彈
  'crystal': 'weapon-crystal-prism',    // 水晶       → 水晶稜鏡彈
};

/** 正式魔王總數(圖鑑 N/TOTAL 用)。= BOSS_WEAPON_UNLOCK 的 key 數。 */
export const TOTAL_BOSS_COUNT = Object.keys(BOSS_WEAPON_UNLOCK).length;

/**
 * 打贏某隻魔王應解鎖的武器類型 id(查不到 → null,例:tutorial-boss / 未知 id)。
 * 純函式:給 result-scene 在 boss 首殺時查「該發哪個武器」。
 */
export function weaponUnlockForBoss(bossId: string | null | undefined): string | null {
  if (!bossId) return null;
  return BOSS_WEAPON_UNLOCK[bossId] ?? null;
}
